<template>
    <div id="app">
        <div class="popover-backdrop" v-if="dateBadgeOpen || resourcesOpen || rateInfoOpen" v-teleport
            @click="dateBadgeOpen = false; resourcesOpen = false; rateInfoOpen = false;"></div>
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>📊</span></div>
                    <h1 class="dashboard-title">SARS Dashboard</h1>
                </div>
                <div class="header-controls">
                    <div class="date-range-badge" @click="dateBadgeOpen = !dateBadgeOpen">
                        <span>📅</span>
                        <span>{{ currentPeriodLabel }}</span>
                        <span class="date-range-chevron" :class="{ open: dateBadgeOpen }">▾</span>

                        <div class="date-range-popover" v-if="dateBadgeOpen" @click.stop>
                            <div class="date-quick-buttons">
                                <button v-for="mode in filterModes" :key="mode.value"
                                    :class="['date-quick-btn', { active: filterMode === mode.value }]"
                                    @click="filterMode = mode.value">
                                    {{ mode.label }}
                                </button>
                            </div>
                            <div class="popover-controls" v-if="filterMode === 'month'">
                                <select v-model.number="params.year" class="specific-date-input">
                                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                                </select>
                                <select v-model.number="params.month" class="specific-date-input">
                                    <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                                </select>
                            </div>
                            <div class="popover-controls" v-else-if="filterMode === 'year'">
                                <select v-model.number="params.year" class="specific-date-input">
                                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                                </select>
                            </div>
                            <div class="popover-controls" v-else>
                                <input type="date" v-model="params.fromDate" class="specific-date-input" />
                                <input type="date" v-model="params.toDate" class="specific-date-input" />
                            </div>
                            <button class="popover-apply-btn" :disabled="loading" @click="dateBadgeOpen = false; loadData();">Apply</button>
                        </div>
                    </div>
                    <div class="resources-badge" @click="resourcesOpen = !resourcesOpen">
                        <span>ℹ️</span>
                        <span>Resources</span>
                        <span class="date-range-chevron" :class="{ open: resourcesOpen }">▾</span>

                        <div class="resources-popover" v-if="resourcesOpen" @click.stop>
                            <a class="resources-link" href="/docs/FAMS-SARS-Diesel-Rebate-Solution.pdf" target="_blank" rel="noopener">
                                <span>📄</span> FAMS SARS Diesel Rebate Solution
                            </a>
                            <a class="resources-link" href="/docs/FAMS-SARS-One-Pager.pdf" target="_blank" rel="noopener">
                                <span>📄</span> FAMS SARS One-Pager
                            </a>
                            <a class="resources-link" href="https://www.sars.gov.za/customs-and-excise/excise/diesel-refund-system/" target="_blank" rel="noopener">
                                <span>🔗</span> SARS Diesel Refund System
                            </a>
                        </div>
                    </div>
                    <div class="live-indicator">
                        <div class="live-dot"></div>
                        <span style="color:white;font-size:0.875rem;">Live</span>
                    </div>
                    <button class="header-refresh-btn" :disabled="loading" @click="loadData"><span>🔄</span> Refresh</button>
                </div>
            </div>
        </div>

        <div class="main-content">

            <div v-if="loading" class="loading-overlay-inline"><div class="loader"></div></div>

            <template v-else-if="rows.length">
                <!-- Hero metric -->
                <div class="hero-banner">
                    <div class="hero-main">
                        <div class="hero-label-row">
                            <div class="hero-label">Total Possible SARS Rebate</div>
                            <div class="rate-info-badge" @click.stop="rateInfoOpen = !rateInfoOpen">
                                <span>ⓘ</span>
                                <div class="rate-info-popover" v-if="rateInfoOpen" @click.stop>
                                    <div class="rate-info-heading">How this is calculated</div>
                                    <p><strong>Total Possible SARS Rebate</strong> = Total Litres Dispensed by <em>eligible</em> equipment × the Rebate Rate per Litre. Only equipment flagged as eligible for the current period contributes to this figure.</p>
                                    <div class="rate-info-heading">Where the rate comes from</div>
                                    <p>R {{ rebateRatePerLitre.toFixed(3) }} / litre = Fuel Levy (78.8 c/L) + RAF Levy (225.0 c/L), the published SARS diesel refund rate effective 1 April 2026 at 100% of eligible consumption (previously 80%).</p>
                                    <div class="rate-info-heading">Applied to this period</div>
                                    <p>{{ totals.eligibleCount }} eligible equipment dispensed {{ formatLitres(totals.totalLitres) }} in {{ currentPeriodLabel }}, giving {{ formatLitres(totals.totalLitres) }} × R {{ rebateRatePerLitre.toFixed(3) }} = <strong>{{ formatCurrency(totals.totalSars) }}</strong> total possible rebate.</p>
                                    <p class="rate-info-note">This rate is user-editable on the SARS Tracker page and shared across both pages. Always confirm against the current SARS Schedule 6 rate before relying on these figures, since it changes whenever the underlying levies are amended.</p>
                                </div>
                            </div>
                        </div>
                        <div class="hero-value">{{ formatCurrency(totals.totalSars) }}</div>
                        <div class="hero-sub">at R {{ rebateRatePerLitre.toFixed(3) }} / litre · {{ formatLitres(totals.totalLitres) }} dispensed</div>
                    </div>
                    <div class="hero-icon">💰</div>
                </div>

                <!-- KPI cards -->
                <div class="kpi-grid">
                    <div class="kpi-card kpi-card-clickable" title="Click to view all equipment, highest litres first" @click="openKpiBreakdown('litres')">
                        <div class="kpi-icon kpi-icon-orange">⛽</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ formatLitres(totals.totalLitres) }}</div>
                            <div class="kpi-label">Total Litres Dispensed</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view all equipment" @click="openKpiBreakdown('equipment')">
                        <div class="kpi-icon kpi-icon-blue">🚚</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ filteredRows.length }}</div>
                            <div class="kpi-label">Total Equipment</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view eligible equipment, highest litres first" @click="openKpiBreakdown('eligible')">
                        <button class="kpi-eye-btn" title="View eligible equipment" @click.stop="openKpiBreakdown('eligible')">👁️</button>
                        <div class="kpi-icon kpi-icon-green">✅</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ totals.eligibleCount }}</div>
                            <div class="kpi-label">Eligible Equipment</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view non-eligible equipment, highest litres first" @click="openKpiBreakdown('nonEligible')">
                        <div class="kpi-icon kpi-icon-red">🚫</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ totals.nonEligibleCount }}</div>
                            <div class="kpi-label">Non-Eligible Equipment</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view active equipment, highest litres first" @click="openKpiBreakdown('active')">
                        <div class="kpi-icon kpi-icon-teal">🟢</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ totals.compliance.Active }}</div>
                            <div class="kpi-label">Active Equipment</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view all equipment, most transactions first" @click="openKpiBreakdown('transactions')">
                        <div class="kpi-icon kpi-icon-indigo">🧾</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ totals.totalTransactions }}</div>
                            <div class="kpi-label">Total Transactions</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Fuel Levy + RAF Levy, at 100% of eligible consumption effective 1 April 2026. Click for details." @click="openRateInfo">
                        <div class="kpi-icon kpi-icon-purple">💵</div>
                        <div class="kpi-body">
                            <div class="kpi-value">R {{ rebateRatePerLitre.toFixed(3) }}</div>
                            <div class="kpi-label">Rebate Rate / Litre</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view all equipment, highest SARS claim first" @click="openKpiBreakdown('avgSars')">
                        <div class="kpi-icon kpi-icon-orange">📐</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ formatCurrency(avgSarsPerEquipment) }}</div>
                            <div class="kpi-label">Avg SARS / Equipment</div>
                        </div>
                    </div>
                    <div class="kpi-card kpi-card-clickable" title="Click to view all equipment, highest avg litres/transaction first" @click="openKpiBreakdown('avgLitres')">
                        <div class="kpi-icon kpi-icon-blue">🔁</div>
                        <div class="kpi-body">
                            <div class="kpi-value">{{ formatLitres(avgLitresPerTransaction) }}</div>
                            <div class="kpi-label">Avg Litres / Transaction</div>
                        </div>
                    </div>
                </div>

                <!-- KPI breakdown popup -->
                <div class="simple-popup" v-if="kpiBreakdownOpen" v-teleport @click.self="closeKpiBreakdown">
                    <div class="popup-content">
                        <div class="popup-header">
                            <h3>📊 {{ kpiBreakdownTitle }}</h3>
                            <button class="close-btn" @click="closeKpiBreakdown">✕</button>
                        </div>
                        <div class="popup-body">
                            <table class="data-table" v-if="kpiBreakdownRows.length">
                                <thead>
                                    <tr><th>Equipment</th><th>Master Group</th><th>{{ kpiBreakdownMetricLabel }}</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in kpiBreakdownRows" :key="item.row.equipmentID">
                                        <td>{{ item.row.equipment }}</td>
                                        <td>{{ item.row.masterEquipmentGroup || 'N/A' }}</td>
                                        <td>{{ formatKpiMetric(item.metric) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div class="empty-state" v-else>
                                <div class="empty-description">No equipment matches this filter</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Analytics -->
                <div class="section-heading">📊 Analytics</div>

                <div class="analytics-grid">
                    <div class="chart-card analytics-trend-card">
                        <div class="chart-header chart-header-row">
                            <h3 class="chart-title">SARS Rebate Trend</h3>
                            <div class="chart-header-total">
                                <span class="chart-header-total-label">Total Rebate</span>
                                <span class="chart-header-total-value">{{ formatCurrency(totals.totalSars) }}</span>
                            </div>
                        </div>
                        <div class="chart-body">
                            <div v-if="trendLoading" class="loading-overlay-inline"><div class="loader"></div></div>
                            <vue-apex-charts v-else type="area" height="300" :options="rebateTrendChartOptions" :series="rebateTrendChartSeries" />
                        </div>
                    </div>
                    <div class="chart-card">
                        <div class="chart-header"><h3 class="chart-title">Rebate by Equipment <span class="chart-title-note">(Top 5)</span></h3></div>
                        <div class="chart-body">
                            <div class="rebate-bar-list" v-if="topEquipmentByRebate.length">
                                <div class="rebate-bar-row" v-for="item in topEquipmentByRebate.slice(0, 5)" :key="item.row.equipmentID">
                                    <div class="rebate-bar-label" :title="item.row.equipment">{{ item.row.equipment }}</div>
                                    <div class="rebate-bar-track">
                                        <div class="rebate-bar-fill" :style="{ width: (topEquipmentByRebateMax ? (item.rebate / topEquipmentByRebateMax * 100) : 0) + '%' }"></div>
                                    </div>
                                    <div class="rebate-bar-value">{{ formatCurrency(item.rebate) }}</div>
                                </div>
                            </div>
                            <div class="empty-state" v-else>
                                <div class="empty-description">No data available</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 6-Month Trend -->
                <div class="chart-card chart-card-wide" style="margin-bottom:20px;">
                    <div class="chart-body">
                        <div class="trend-metric-picker">
                            <span class="trend-metric-picker-label">Show on chart:</span>
                            <button v-for="m in trendMetricOptions" :key="m.key"
                                :class="['chip-btn', { active: selectedTrendMetrics.indexOf(m.key) !== -1 }]"
                                :style="selectedTrendMetrics.indexOf(m.key) !== -1 ? { background: m.color, borderColor: m.color } : {}"
                                @click="toggleTrendMetric(m.key)">
                                {{ m.label }}
                            </button>
                        </div>
                        <div v-if="trendLoading" class="loading-overlay-inline"><div class="loader"></div></div>
                        <vue-apex-charts v-else type="line" height="300" :options="periodComparisonChartOptions" :series="periodComparisonChartSeries" />
                    </div>
                </div>

                <!-- Top Rebate Generators -->
                <div class="section-heading">🏆 Top Rebate Generators</div>
                <div class="leaderboard-card">
                    <div class="driver-table-wrapper">
                        <table class="data-table">
                            <thead>
                                <tr><th>#</th><th>Equipment</th><th>Master Group</th><th>Total Litres</th><th>Transactions</th><th>Estimated Rebate</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in topEquipmentByRebate" :key="item.row.equipmentID">
                                    <td class="rank-cell">
                                        <span v-if="index === 0">🥇</span>
                                        <span v-else-if="index === 1">🥈</span>
                                        <span v-else-if="index === 2">🥉</span>
                                        <span v-else>{{ index + 1 }}</span>
                                    </td>
                                    <td>{{ item.row.equipment }}</td>
                                    <td>{{ item.row.masterEquipmentGroup || 'N/A' }}</td>
                                    <td>{{ formatLitres(item.row.totalLitresDispensed) }}</td>
                                    <td>{{ item.row.transactionCount }}</td>
                                    <td>{{ formatCurrency(item.rebate) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Breakdown -->
                <div class="section-heading">Breakdown</div>
                <div class="charts-section">
                    <div class="chart-card">
                        <div class="chart-header"><h3 class="chart-title">🗂️ Litres by Master Equipment Group <span class="chart-title-note">(Top 5 + Other)</span></h3></div>
                        <div class="chart-body">
                            <vue-apex-charts type="bar" height="260" :options="masterGroupChartOptions" :series="masterGroupChartSeries" />
                        </div>
                    </div>
                    <div class="chart-card">
                        <div class="chart-header"><h3 class="chart-title">🏷️ Litres by Cost Centre <span class="chart-title-note">(Top 5 + Other)</span></h3></div>
                        <div class="chart-body">
                            <vue-apex-charts type="bar" height="260" :options="costCentreChartOptions" :series="costCentreChartSeries" />
                        </div>
                    </div>
                    <div class="chart-card" v-if="topHoursEquipmentList.length || topKmEquipmentList.length">
                        <div class="chart-header"><h3 class="chart-title">⚙️ Efficiency Highlights</h3></div>
                        <div class="chart-body efficiency-highlights">
                            <div v-if="topHoursEquipmentList.length">
                                <div class="stat-label">⚡ Best Efficiency (L/hr)</div>
                                <div class="efficiency-highlight" v-for="(r, i) in topHoursEquipmentList" :key="'hr-' + i">
                                    <div class="stat-value" style="font-size:1rem;">{{ i + 1 }}. {{ r.equipment }}</div>
                                    <div class="stat-change">{{ r.litresPerHour }} L/hr · {{ formatLitres(r.totalLitres) }} over {{ r.totalHours }} hrs</div>
                                </div>
                            </div>
                            <div v-if="topKmEquipmentList.length">
                                <div class="stat-label">🚗 Best Fuel Economy (L/km)</div>
                                <div class="efficiency-highlight" v-for="(r, i) in topKmEquipmentList" :key="'km-' + i">
                                    <div class="stat-value" style="font-size:1rem;">{{ i + 1 }}. {{ r.equipment }}</div>
                                    <div class="stat-change">{{ r.litresPerKM }} L/km ({{ r.kmPerLitre }} km/L) · lower is better</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Driver Performance -->
                <template v-if="topDriver">
                    <div class="section-heading">Driver Performance</div>
                    <div class="leaderboard-card">
                        <div class="top-driver-banner">
                            <div class="top-driver-medal">🏆</div>
                            <div class="top-driver-body">
                                <div class="top-driver-label">Top Driver</div>
                                <div class="top-driver-name">{{ topDriver.driverName }}</div>
                                <div class="top-driver-stats">
                                    {{ formatLitres(topDriver.totalLitresDispensed) }} · {{ topDriver.transactionCount }} transactions ·
                                    {{ formatLitres(topDriver.avgLitresPerTransaction) }}/txn
                                </div>
                            </div>
                        </div>
                        <div class="driver-table-wrapper">
                            <table class="data-table">
                                <thead>
                                    <tr><th>Rank</th><th>Driver</th><th>Litres</th><th>Transactions</th><th>Avg / Transaction</th><th>Last Dispensing</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(driver, index) in drivers" :key="driver.driverID">
                                        <td class="rank-cell">
                                            <span v-if="index === 0">🥇</span>
                                            <span v-else-if="index === 1">🥈</span>
                                            <span v-else-if="index === 2">🥉</span>
                                            <span v-else>{{ index + 1 }}</span>
                                        </td>
                                        <td>{{ driver.driverName }}</td>
                                        <td>{{ formatLitres(driver.totalLitresDispensed) }}</td>
                                        <td>{{ driver.transactionCount }}</td>
                                        <td>{{ formatLitres(driver.avgLitresPerTransaction) }}</td>
                                        <td>{{ formatDate(driver.lastDispensingDate) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>
            </template>

            <div v-else class="empty-state">
                <div class="empty-icon">📭</div>
                <div class="empty-title">No Data For This Month</div>
                <div class="empty-description">No SARS activity found for the current month yet. Click <strong>Refresh</strong> to try again, or use the SARS Tracker to view a different period.</div>
            </div>
        </div>
    </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
    name: 'SARSDashboard',
    components: { VueApexCharts },
    // The admin layout's route-transition wrapper applies a CSS transform,
    // which turns it into the containing block for any position:fixed
    // descendant - so full-screen overlays (popups, backdrops) end up
    // sized/positioned relative to that wrapper instead of the real
    // viewport, clipping and misaligning them. Moving the element straight
    // onto <body> on insert sidesteps that entirely; Vue still removes it
    // correctly later since removeChild is called on el.parentNode.
    directives: {
        teleport: {
            inserted: function (el) { document.body.appendChild(el); }
        }
    },

    data: function () {
        var today = new Date();
        var savedRate = parseFloat(window.localStorage ? localStorage.getItem('sarsRebateRatePerLitre') : null);
        return {
            loading: false,
            statusFilter: '',
            masterGroupFilter: '',
            costCentreFilter: '',
            rows: [],
            drivers: [],
            efficiencyHours: [],
            efficiencyKM: [],
            trendData: [],
            trendLoading: false,
            dateBadgeOpen: false,
            resourcesOpen: false,
            rateInfoOpen: false,
            kpiBreakdownOpen: null,
            selectedTrendMetrics: ['totalLitres', 'totalSars'],
            trendMetricOptions: [
                {
                    key: 'totalLitres', label: 'Litres Dispensed', color: '#D2691E', markerColor: '#FFC107',
                    format: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' L'; }
                },
                {
                    key: 'totalSars', label: 'SARS Claim', color: '#27AE60', markerColor: '#8BE0B0',
                    format: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); },
                    tooltipFormat: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 }); }
                },
                {
                    key: 'totalTransactions', label: 'Transactions', color: '#2196F3', markerColor: '#90CAF9',
                    format: function (val) { return Number(val).toLocaleString(); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString() + ' transactions'; }
                },
                {
                    key: 'equipmentCount', label: 'Equipment Reporting', color: '#9C27B0', markerColor: '#CE93D8',
                    format: function (val) { return Number(val).toLocaleString(); },
                    tooltipFormat: function (val) { return Number(val).toLocaleString() + ' equipment'; }
                }
            ],
            // R3.038/L = Fuel Levy (78.8c/L) + RAF Levy (225.0c/L), the
            // published SARS diesel refund rate effective 1 April 2026 at
            // 100% of eligible consumption (previously 80%). Source: FAMS
            // SARS Diesel Refund Solution one-pager. Always confirm against
            // the current SARS Schedule 6 rate, since it changes whenever
            // the underlying levies are amended.
            rebateRatePerLitre: isNaN(savedRate) ? 3.038 : savedRate,
            filterMode: 'month',
            filterModes: [
                { value: 'month', label: 'Month' },
                { value: 'range', label: 'Date Range' },
                { value: 'year', label: 'Whole Year' }
            ],
            monthOptions: [
                { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
                { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
                { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
                { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
            ],
            params: { year: today.getFullYear(), month: today.getMonth() + 1, fromDate: '', toDate: '' }
        };
    },

    computed: {
        yearOptions: function () {
            var current = new Date().getFullYear();
            var years = [];
            for (var y = current; y >= current - 4; y--) years.push(y);
            return years;
        },
        currentPeriodLabel: function () {
            if (this.filterMode === 'year') return String(this.params.year);
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return 'Select dates';
                return this.params.fromDate + ' → ' + this.params.toDate;
            }
            var match = this.monthOptions.filter(function (m) { return m.value === this.params.month; }, this)[0];
            return (match ? match.label : '') + ' ' + this.params.year;
        },

        masterGroupOptions: function () {
            var values = this.rows.map(function (r) { return r.masterEquipmentGroup; }).filter(function (v) { return v; });
            return values.filter(function (v, i) { return values.indexOf(v) === i; }).sort();
        },
        costCentreOptions: function () {
            var values = this.rows.map(function (r) { return r.costCentre; }).filter(function (v) { return v; });
            return values.filter(function (v, i) { return values.indexOf(v) === i; }).sort();
        },

        filteredRows: function () {
            var self = this;
            var result = this.rows;
            if (this.statusFilter) {
                result = result.filter(function (r) { return r.sarsEligible === self.statusFilter; });
            }
            if (this.masterGroupFilter) {
                result = result.filter(function (r) { return r.masterEquipmentGroup === self.masterGroupFilter; });
            }
            if (this.costCentreFilter) {
                result = result.filter(function (r) { return r.costCentre === self.costCentreFilter; });
            }
            return result;
        },

        totals: function () {
            var self = this;
            var totalLitres = 0, totalSars = 0, eligibleCount = 0, nonEligibleCount = 0, totalTransactions = 0;
            var compliance = { Active: 0, Inactive: 0, Stale: 0 };
            this.filteredRows.forEach(function (row) {
                totalLitres += Number(row.totalLitresDispensed) || 0;
                totalSars += self.computedSarsAmount(row);
                totalTransactions += Number(row.transactionCount) || 0;
                if (row.sarsEligible === 'Yes') eligibleCount++; else nonEligibleCount++;
                if (compliance[row.complianceStatus] !== undefined) compliance[row.complianceStatus]++;
            });
            return { totalLitres: totalLitres, totalSars: totalSars, eligibleCount: eligibleCount, nonEligibleCount: nonEligibleCount, totalTransactions: totalTransactions, compliance: compliance };
        },

        // Horizontal bar - ranked comparisons read better as bars than
        // wedges, especially once "Other" is folded in.
        masterGroupChartSeries: function () {
            var groups = this.topGroupsByLitres('masterEquipmentGroup');
            return [{ name: 'Litres', data: groups.map(function (g) { return Math.round(g.litres); }) }];
        },
        masterGroupChartOptions: function () {
            var groups = this.topGroupsByLitres('masterEquipmentGroup');
            var maxLitres = groups.reduce(function (m, g) { return Math.max(m, g.litres); }, 0);
            return {
                chart: { toolbar: { show: false }, width: '100%', offsetX: 0 },
                colors: ['#D2691E'],
                grid: { padding: { right: 30 } },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
                xaxis: {
                    categories: groups.map(function (g) { return g.name; }),
                    min: 0,
                    max: this.niceAxisMax(maxLitres),
                    tickAmount: 4,
                    forceNiceScale: true,
                    labels: { formatter: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); } }
                },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' L'; } } },
                noData: { text: 'No data available' }
            };
        },

        costCentreChartSeries: function () {
            var groups = this.topGroupsByLitres('costCentre');
            return [{ name: 'Litres', data: groups.map(function (g) { return Math.round(g.litres); }) }];
        },
        costCentreChartOptions: function () {
            var groups = this.topGroupsByLitres('costCentre');
            var maxLitres = groups.reduce(function (m, g) { return Math.max(m, g.litres); }, 0);
            return {
                chart: { toolbar: { show: false }, width: '100%', offsetX: 0 },
                colors: ['#27AE60'],
                grid: { padding: { right: 30 } },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
                xaxis: {
                    categories: groups.map(function (g) { return g.name; }),
                    min: 0,
                    max: this.niceAxisMax(maxLitres),
                    tickAmount: 4,
                    forceNiceScale: true,
                    labels: { formatter: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); } }
                },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: function (val) { return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' L'; } } },
                noData: { text: 'No data available' }
            };
        },

        // Top 10 equipment by SARS amount, ranked - backs both the "Rebate
        // by Equipment" bar list and the "Top Rebate Generators" table.
        topEquipmentByRebate: function () {
            var self = this;
            return this.filteredRows.slice()
                .sort(function (a, b) { return self.computedSarsAmount(b) - self.computedSarsAmount(a); })
                .slice(0, 10)
                .map(function (r) { return { row: r, rebate: self.computedSarsAmount(r) }; });
        },
        topEquipmentByRebateMax: function () {
            return this.topEquipmentByRebate.length ? this.topEquipmentByRebate[0].rebate : 0;
        },

        // Single-line area trend of the SARS rebate over the last 6
        // months, feeding "SARS Rebate Trend".
        rebateTrendChartSeries: function () {
            return [{ name: 'SARS Rebate', data: this.trendData.map(function (t) { return Math.round(t.totalSars); }) }];
        },
        rebateTrendChartOptions: function () {
            return {
                chart: { toolbar: { show: false } },
                colors: ['#FF8C00'],
                stroke: { width: 3, curve: 'smooth' },
                fill: {
                    type: 'gradient',
                    gradient: { shadeIntensity: 1, opacityFrom: .45, opacityTo: .05, stops: [0, 90, 100] }
                },
                markers: { size: 5, strokeWidth: 2, strokeColors: '#fff', colors: ['#FF8C00'], hover: { size: 7 } },
                grid: { borderColor: '#eef1f4' },
                xaxis: {
                    categories: this.trendData.map(function (t) { return t.label; }),
                    labels: { style: { fontSize: '12px' } }
                },
                yaxis: {
                    labels: { formatter: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }); } }
                },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: function (val) { return 'R ' + Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 }); } } },
                noData: { text: 'No data available' }
            };
        },

        avgSarsPerEquipment: function () {
            return this.filteredRows.length ? this.totals.totalSars / this.filteredRows.length : 0;
        },
        avgLitresPerTransaction: function () {
            return this.totals.totalTransactions ? this.totals.totalLitres / this.totals.totalTransactions : 0;
        },

        topDriver: function () {
            return this.drivers.length ? this.drivers[0] : null;
        },

        // Only the metrics the user has ticked on, in the fixed order
        // defined by trendMetricOptions - mirrors SARS Tracker's picker.
        selectedTrendMetricConfigs: function () {
            var self = this;
            return this.trendMetricOptions.filter(function (m) { return self.selectedTrendMetrics.indexOf(m.key) !== -1; });
        },
        periodComparisonChartSeries: function () {
            return this.selectedTrendMetricConfigs.map(function (m) {
                return { name: m.label, data: this.trendData.map(function (t) { return Math.round(Number(t[m.key]) || 0); }) };
            }, this);
        },
        periodComparisonChartOptions: function () {
            var self = this;
            var configs = this.selectedTrendMetricConfigs;
            return {
                chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                title: {
                    text: '6-Month Trend',
                    align: 'center',
                    style: { fontSize: '18px', fontWeight: 700, color: '#2c3e50' }
                },
                colors: configs.map(function (m) { return m.color; }),
                stroke: { width: 4, curve: 'straight' },
                markers: {
                    size: 7, strokeWidth: 2, strokeColors: '#fff',
                    colors: configs.map(function (m) { return m.markerColor; }),
                    hover: { size: 9 }
                },
                grid: {
                    borderColor: '#dcdfe3',
                    xaxis: { lines: { show: true } },
                    yaxis: { lines: { show: true } }
                },
                xaxis: {
                    categories: this.trendData.map(function (t) { return t.label; }),
                    labels: { style: { fontSize: '13px', fontWeight: 700, colors: '#2c3e50' } }
                },
                dataLabels: { enabled: false },
                yaxis: configs.map(function (m, idx) {
                    if (idx > 1) return { show: false, min: 0 };
                    return {
                        opposite: idx === 1,
                        min: 0,
                        max: self.trendAxisMax(m.key, idx === 0 ? 1.15 : 1.75),
                        forceNiceScale: true,
                        tickAmount: 5,
                        title: { text: m.label, style: { fontWeight: 700 } },
                        labels: { style: { fontWeight: 600 }, formatter: m.format }
                    };
                }),
                tooltip: {
                    shared: true,
                    x: { show: true },
                    y: configs.map(function (m) { return { formatter: m.tooltipFormat }; })
                },
                legend: {
                    position: 'top', horizontalAlign: 'center',
                    fontSize: '13px', fontWeight: 600,
                    markers: { width: 14, height: 14, radius: 3 },
                    itemMargin: { horizontal: 10 }
                },
                noData: { text: 'No data available' }
            };
        },

        // Backs the click-through popup on every KPI card - title plus the
        // ranked row list, both driven off which card was clicked.
        kpiBreakdownTitle: function () {
            var map = {
                litres: 'Total Litres Dispensed — By Equipment',
                equipment: 'Total Equipment',
                eligible: 'Eligible Equipment',
                nonEligible: 'Non-Eligible Equipment',
                active: 'Active Equipment',
                transactions: 'Total Transactions — By Equipment',
                avgSars: 'Avg SARS / Equipment — Ranked',
                avgLitres: 'Avg Litres / Transaction — Ranked'
            };
            return this.kpiBreakdownOpen ? map[this.kpiBreakdownOpen] : '';
        },
        kpiBreakdownMetricLabel: function () {
            var map = {
                litres: 'Litres', equipment: 'Status', eligible: 'Litres', nonEligible: 'Litres',
                active: 'Litres', transactions: 'Transactions', avgSars: 'SARS Amount', avgLitres: 'Avg L / Txn'
            };
            return this.kpiBreakdownOpen ? map[this.kpiBreakdownOpen] : '';
        },
        // "Total Equipment" is a roster, not a litres ranking - it lists
        // every piece of equipment alphabetically with its eligibility and
        // compliance status, which is what that card actually counts.
        // Every other card ranks by the number it represents.
        kpiBreakdownRows: function () {
            if (!this.kpiBreakdownOpen) return [];
            var self = this;
            var type = this.kpiBreakdownOpen;
            var rows = this.filteredRows.slice();
            if (type === 'eligible') rows = rows.filter(function (r) { return r.sarsEligible === 'Yes'; });
            if (type === 'nonEligible') rows = rows.filter(function (r) { return r.sarsEligible !== 'Yes'; });
            if (type === 'active') rows = rows.filter(function (r) { return r.complianceStatus === 'Active'; });

            if (type === 'equipment') {
                return rows
                    .sort(function (a, b) { return (a.equipment || '').localeCompare(b.equipment || ''); })
                    .map(function (r) {
                        var status = r.sarsEligible === 'Yes' ? 'Eligible' : 'Non-Eligible';
                        return { row: r, metric: status + ' · ' + (r.complianceStatus || 'Unknown') };
                    });
            }

            var metricFn;
            if (type === 'transactions') metricFn = function (r) { return Number(r.transactionCount) || 0; };
            else if (type === 'avgSars') metricFn = function (r) { return self.computedSarsAmount(r); };
            else if (type === 'avgLitres') metricFn = function (r) { return r.transactionCount ? (Number(r.totalLitresDispensed) || 0) / r.transactionCount : 0; };
            else metricFn = function (r) { return Number(r.totalLitresDispensed) || 0; };

            return rows
                .sort(function (a, b) { return metricFn(b) - metricFn(a); })
                .map(function (r) { return { row: r, metric: metricFn(r) }; });
        },

        // Top 3 by L/hr, excluding rows with no recorded hours - those
        // produce nonsensical near-zero or infinite ratios that would
        // otherwise wrongly surface as the "best" result.
        topHoursEquipmentList: function () {
            return this.efficiencyHours
                .filter(function (r) { return Number(r.totalHours) > 0 && Number(r.litresPerHour) > 0; })
                .slice()
                .sort(function (a, b) { return b.litresPerHour - a.litresPerHour; })
                .slice(0, 3);
        },

        // Top 3 by L/km (lower is better), excluding rows with no recorded
        // km - same zero-denominator problem as hours.
        topKmEquipmentList: function () {
            return this.efficiencyKM
                .filter(function (r) { return Number(r.totalKM) > 0 && Number(r.litresPerKM) > 0; })
                .slice()
                .sort(function (a, b) { return a.litresPerKM - b.litresPerKM; })
                .slice(0, 3);
        }
    },

    methods: {
        formatLitres: function (value) {
            if (value == null) return '0 L';
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' L';
        },
        formatCurrency: function (value) {
            if (value == null) return 'R 0.00';
            return 'R ' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatDate: function (value) {
            if (!value) return 'N/A';
            return new Date(value).toLocaleDateString();
        },
        // Top contributors by litres, grouped by dimension - far more
        // informative than an Eligible/Compliance split, which is usually
        // 100/0 for most accounts (every equipment row already shares that
        // exact count via the KPI cards above) and shows almost no variance.
        // The smallest groups beyond the top 5 are folded into "Other" so
        // the chart stays readable while still reflecting every row.
        topGroupsByLitres: function (field) {
            var groups = {};
            var order = [];
            this.filteredRows.forEach(function (row) {
                var name = row[field] || 'Unspecified';
                if (!groups[name]) { groups[name] = 0; order.push(name); }
                groups[name] += Number(row.totalLitresDispensed) || 0;
            });
            var all = order.map(function (name) { return { name: name, litres: groups[name] }; })
                .sort(function (a, b) { return b.litres - a.litres; });
            var top = all.slice(0, 5);
            var rest = all.slice(5).reduce(function (sum, g) { return sum + g.litres; }, 0);
            if (rest > 0) top.push({ name: 'Other', litres: rest });
            return top;
        },
        // Rounds a chart axis max UP to a clean, even number (1/2/5 x a
        // power of ten - 2,000 / 5,000 / 20,000, never 4,371 or 14,569) so
        // the auto-generated ticks land on values worth reading, while
        // still guaranteeing the bound is >= the actual data max.
        niceAxisMax: function (value) {
            if (!value || value <= 0) return 10;
            var magnitude = Math.pow(10, Math.floor(Math.log10(value)));
            var residual = value / magnitude;
            var niceResidual = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
            return niceResidual * magnitude;
        },
        // SARSAmountDue from the API is fixed at R3.00/litre server-side. The
        // rate is user-editable here, so recompute it client-side (litres x
        // rate, only for eligible equipment) instead of trusting that field -
        // this is what makes changing the rate actually change every figure
        // on the page, not just the label.
        computedSarsAmount: function (row) {
            if (!row || row.sarsEligible !== 'Yes') return 0;
            return (Number(row.totalLitresDispensed) || 0) * (Number(this.rebateRatePerLitre) || 0);
        },
        // Headroom-padded max for one trend series' y-axis - see the note
        // on periodComparisonChartOptions for why the two visible axes need
        // different padding multipliers (proportional series otherwise draw
        // as a single overlapping line).
        trendAxisMax: function (field, headroom) {
            var max = this.trendData.reduce(function (m, t) { return Math.max(m, Number(t[field]) || 0); }, 0);
            return max > 0 ? max * headroom : 1;
        },
        // Lets the user pick which metrics plot on the 6-month trend chart.
        // Always keeps at least one selected.
        toggleTrendMetric: function (key) {
            var idx = this.selectedTrendMetrics.indexOf(key);
            if (idx !== -1) {
                if (this.selectedTrendMetrics.length === 1) return;
                this.selectedTrendMetrics.splice(idx, 1);
            } else {
                this.selectedTrendMetrics.push(key);
            }
        },
        openKpiBreakdown: function (type) {
            this.kpiBreakdownOpen = type;
        },
        closeKpiBreakdown: function () {
            this.kpiBreakdownOpen = null;
        },
        formatKpiMetric: function (value) {
            var type = this.kpiBreakdownOpen;
            if (type === 'equipment') return value;
            if (type === 'avgSars') return this.formatCurrency(value);
            if (type === 'transactions') return value;
            return this.formatLitres(value);
        },
        openRateInfo: function () {
            this.rateInfoOpen = true;
        },
        closeRateInfo: function () {
            this.rateInfoOpen = false;
        },
        buildQueryString: function () {
            if (this.filterMode === 'range') {
                if (!this.params.fromDate || !this.params.toDate) return null;
                return 'startDate=' + encodeURIComponent(this.params.fromDate) + '&endDate=' + encodeURIComponent(this.params.toDate);
            }
            if (this.filterMode === 'year') {
                return 'startDate=' + this.params.year + '-01-01&endDate=' + this.params.year + '-12-31';
            }
            return 'year=' + this.params.year + '&month=' + this.params.month;
        },
        loadData: function () {
            var self = this;
            var qs = self.buildQueryString();
            if (qs === null) { alert('Please select both a From Date and To Date.'); return; }
            self.loading = true;
            self.rows = [];
            self.drivers = [];
            self.efficiencyHours = [];
            self.efficiencyKM = [];

            var onEquipmentSuccess = function (response) { if (response.data) self.rows = response.data; };
            var onDriverSuccess = function (response) { if (response.data) self.drivers = response.data; };
            var onHoursSuccess = function (response) { if (response.data) self.efficiencyHours = response.data; };
            var onKmSuccess = function (response) { if (response.data) self.efficiencyKM = response.data; };

            self.$ajaxGet(self, 'SARSEquipmentTracking?' + qs, onEquipmentSuccess, function () { self.loading = false; });
            self.$ajaxGet(self, 'SARSEquipmentTracking/DriverPerformance?' + qs, onDriverSuccess, function () {});
            self.$ajaxGet(self, 'SARSEquipmentTracking/EquipmentEfficiencyHours?' + qs, onHoursSuccess, function () {});
            self.$ajaxGet(self, 'SARSEquipmentTracking/EquipmentEfficiencyKM?' + qs, onKmSuccess, function () {});
            self.loadTrendData();
        },
        // Last 6 calendar months up to and including the current one,
        // reduced to their SARS totals - feeds the "SARS Rebate Trend"
        // chart with real history instead of a single snapshot.
        loadTrendData: function () {
            var self = this;
            var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            var today = new Date();
            var periods = [];
            for (var i = 5; i >= 0; i--) {
                var d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                periods.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: monthNames[d.getMonth()] + ' ' + d.getFullYear() });
            }

            self.trendLoading = true;
            self.trendData = periods.map(function (p) {
                return { label: p.label, totalSars: 0, totalLitres: 0, totalTransactions: 0, equipmentCount: 0 };
            });

            var pending = periods.length;
            periods.forEach(function (p, idx) {
                var onSuccess = function (response) {
                    if (!response.data) return;
                    var totals = self.periodTotals(response.data);
                    self.$set(self.trendData, idx, {
                        label: p.label, totalSars: totals.totalSars, totalLitres: totals.totalLitres,
                        totalTransactions: totals.totalTransactions, equipmentCount: totals.equipmentCount
                    });
                };
                var onFinally = function () {
                    pending--;
                    if (pending === 0) self.trendLoading = false;
                };
                self.$ajaxGet(self, 'SARSEquipmentTracking?year=' + p.year + '&month=' + p.month, onSuccess, onFinally);
            });
        },
        // Reduces a raw equipment-row array to the same aggregate totals
        // used everywhere else on the page - shared by the 6-month trend
        // fetch above so history and "now" are computed identically.
        periodTotals: function (rows) {
            var self = this;
            var totalLitres = 0, totalSars = 0, totalTransactions = 0;
            rows.forEach(function (row) {
                totalLitres += Number(row.totalLitresDispensed) || 0;
                totalSars += self.computedSarsAmount(row);
                totalTransactions += Number(row.transactionCount) || 0;
            });
            return { totalLitres: totalLitres, totalSars: totalSars, totalTransactions: totalTransactions, equipmentCount: rows.length };
        },
        // Closes the date-range/resources popovers on an outside click.
        onDocumentClick: function (e) {
            if (this.dateBadgeOpen && !e.target.closest('.date-range-badge')) this.dateBadgeOpen = false;
            if (this.resourcesOpen && !e.target.closest('.resources-badge')) this.resourcesOpen = false;
            if (this.rateInfoOpen && !e.target.closest('.rate-info-badge')) this.rateInfoOpen = false;
        },
        // The rebate rate is shared across pages via localStorage rather
        // than a backend setting - this page has no input for it anymore,
        // it only ever reads what the SARS Tracker last saved. Without this
        // listener, a rate changed on Tracker in another tab (or another
        // window) would silently never reach an already-open Dashboard tab,
        // leaving it computing every SARS figure off a stale rate.
        onRebateRateStorageChange: function (e) {
            if (e.key !== 'sarsRebateRatePerLitre') return;
            var v = parseFloat(e.newValue);
            if (!isNaN(v) && v >= 0) this.rebateRatePerLitre = v;
        }
    },

    mounted: function () {
        var fmt = function (d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
        var today = new Date();
        this.params.fromDate = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
        this.params.toDate = fmt(today);
        this.loadData();
        window.addEventListener('storage', this.onRebateRateStorageChange);
        document.addEventListener('click', this.onDocumentClick);
    },

    beforeDestroy: function () {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('storage', this.onRebateRateStorageChange);
    }
};
</script>

<style scoped>
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
    flex-shrink: 0;
    position: relative;
    z-index: 45;
}
.popover-backdrop {
    position: fixed; inset: 0; z-index: 40;
    background: rgba(15, 23, 42, .6); backdrop-filter: blur(2px); -webkit-backdrop-filter: blur(2px);
}
.header-content { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0 20px; }
.logo-section { display: flex; align-items: center; gap: 12px; }
.logo-icon { width: 40px; height: 40px; background: white; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
.dashboard-title { color: white; font-size: 1.5rem; font-weight: 600; }
.header-controls { display: flex; align-items: center; gap: 20px; }
.live-indicator { display: flex; align-items: center; gap: 8px; background: rgba(255, 255, 255, .2); padding: 8px 16px; border-radius: 20px; }
.header-refresh-btn {
    padding: 8px 16px; background: rgba(255, 255, 255, .2); color: #fff; border: 1px solid rgba(255, 255, 255, .5);
    border-radius: 20px; font-size: .875rem; font-weight: 500; cursor: pointer; transition: all .2s;
    display: inline-flex; align-items: center; gap: 6px;
}
.header-refresh-btn:hover { background: rgba(255, 255, 255, .35); }
.header-refresh-btn:disabled { opacity: .6; cursor: not-allowed; }

.date-range-badge {
    position: relative; display: flex; align-items: center; gap: 8px; cursor: pointer;
    background: rgba(255, 255, 255, .2); color: #fff; padding: 8px 16px; border-radius: 20px;
    font-size: .875rem; font-weight: 500;
}
.date-range-chevron { transition: transform .2s; font-size: .7rem; }
.date-range-chevron.open { transform: rotate(180deg); }
.date-range-popover {
    position: absolute; top: calc(100% + 10px); right: 0; background: #fff; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .2); padding: 16px; min-width: 260px; z-index: 50; cursor: default;
}
.popover-controls { display: flex; gap: 10px; margin-top: 10px; }
.popover-apply-btn {
    margin-top: 12px; width: 100%; padding: 8px 16px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: #fff; border: none; border-radius: 6px; font-size: .875rem; font-weight: 600; cursor: pointer;
}
.popover-apply-btn:disabled { opacity: .6; cursor: not-allowed; }

.resources-badge {
    position: relative; display: flex; align-items: center; gap: 8px; cursor: pointer;
    background: rgba(255, 255, 255, .2); color: #fff; padding: 8px 16px; border-radius: 20px;
    font-size: .875rem; font-weight: 500;
}
.resources-popover {
    position: absolute; top: calc(100% + 10px); right: 0; background: #fff; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .2); padding: 8px; min-width: 260px; z-index: 50; cursor: default;
}
.resources-link {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 8px;
    color: #334155; font-size: .85rem; font-weight: 500; text-decoration: none; transition: background .15s;
}
.resources-link:hover { background: #fff9f5; color: #D2691E; }
.live-dot { width: 8px; height: 8px; background: #4CAF50; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, .7); }
    70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.main-content { flex: 1; overflow-y: auto; padding: 20px; background: #e2e8f0; }
.main-content > * { max-width: 1600px; margin-left: auto; margin-right: auto; }

.loading-overlay-inline { display: flex; justify-content: center; padding: 60px 0; }
.loader { width: 40px; height: 40px; border: 4px solid #f1f5f9; border-top-color: #D2691E; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.stats-section { background: #fff; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 2px 4px rgba(0, 0, 0, .15); border: 1px solid #cbd5e1; }
.stats-header { padding: 16px 20px; background: #f1f5f9; border-bottom: 1px solid #cbd5e1; border-radius: 12px 12px 0 0; }
.stats-title { font-size: 1.125rem; font-weight: 600; color: #34495E; margin: 0; }

.section-heading {
    font-size: .8rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .06em;
    margin: 28px 2px 12px;
}

.date-quick-buttons { display: flex; gap: 4px; flex-wrap: wrap; }
.date-quick-btn {
    padding: 5px 10px; border: 1px solid #cbd5e1; background: white; border-radius: 5px;
    font-size: .75rem; font-weight: 500; color: #64748b; cursor: pointer; transition: all .2s ease;
}
.date-quick-btn:hover { border-color: #D2691E; color: #D2691E; background: #fff9f5; }
.date-quick-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; box-shadow: 0 2px 8px rgba(210, 105, 30, .2); }

.specific-date-input {
    padding: 5px 10px; border: 1px solid #cbd5e1; border-radius: 5px; font-size: .85rem;
    color: #334155; background: white; width: 100%;
}
.specific-date-input:focus { outline: none; border-color: #D2691E; box-shadow: 0 0 0 2px rgba(210, 105, 30, .15); }

.analytics-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 20px; }
.analytics-trend-card { min-width: 0; }
.chart-header-row { display: flex; justify-content: space-between; align-items: center; }
.chart-header-total { text-align: right; }
.chart-header-total-label { display: block; font-size: .7rem; color: #94a3b8; font-weight: 600; text-transform: uppercase; }
.chart-header-total-value { font-size: 1rem; font-weight: 800; color: #FF8C00; }

.rebate-bar-list { display: flex; flex-direction: column; gap: 14px; }
.rebate-bar-row { display: flex; flex-direction: column; gap: 4px; }
.rebate-bar-label { font-size: .8rem; font-weight: 600; color: #2c3e50; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rebate-bar-track { background: #f1f5f9; border-radius: 6px; height: 10px; overflow: hidden; }
.rebate-bar-fill { background: linear-gradient(90deg, #2196F3, #64B5F6); height: 100%; border-radius: 6px; transition: width .4s ease; }
.rebate-bar-value { font-size: .75rem; font-weight: 700; color: #64748b; align-self: flex-end; }


.hero-banner {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    border-radius: 16px; padding: 26px 30px; margin-bottom: 20px;
    display: flex; align-items: center; justify-content: space-between; gap: 20px;
    box-shadow: 0 8px 24px rgba(210, 105, 30, .28);
}
.hero-label-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.hero-label { color: rgba(255, 255, 255, .85); font-size: .85rem; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 0; }
.hero-value { color: #fff; font-size: 2.6rem; font-weight: 800; line-height: 1.1; }
.hero-sub { color: rgba(255, 255, 255, .85); font-size: .85rem; margin-top: 8px; }
.hero-icon { font-size: 3rem; opacity: .85; flex-shrink: 0; }

.rate-info-badge {
    position: relative; display: inline-flex; align-items: center; justify-content: center;
    width: 20px; height: 20px; border-radius: 50%; background: rgba(255, 255, 255, .25);
    color: #fff; font-size: .8rem; cursor: pointer; flex-shrink: 0;
}
.rate-info-badge:hover { background: rgba(255, 255, 255, .4); }
.rate-info-popover {
    position: absolute; top: calc(100% + 10px); left: 0; background: #fff; border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, .25); padding: 18px; width: 340px; max-width: 80vw;
    z-index: 60; cursor: default; color: #334155;
}
.rate-info-heading { font-size: .78rem; font-weight: 700; color: #D2691E; text-transform: uppercase; letter-spacing: .04em; margin: 12px 0 4px; }
.rate-info-heading:first-child { margin-top: 0; }
.rate-info-popover p { font-size: .82rem; line-height: 1.45; margin: 0 0 8px; }
.rate-info-note { color: #94a3b8; font-size: .74rem !important; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 16px; margin-bottom: 8px; }
.kpi-card {
    background: #fff; border-radius: 14px; padding: 18px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    border: 1px solid #e2e8f0; transition: all .25s; display: flex; align-items: center; gap: 14px;
}
.kpi-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0, 0, 0, .1); }
.kpi-card-clickable { position: relative; cursor: pointer; }
.kpi-eye-btn {
    position: absolute; top: 44px; right: 14px; width: 26px; height: 26px; padding: 0;
    background: #fff; color: #27AE60; border: 1px solid #27AE60; border-radius: 50%;
    font-size: .95rem; line-height: 1; cursor: pointer; transition: all .2s;
    display: inline-flex; align-items: center; justify-content: center; z-index: 1;
}
.kpi-eye-btn:hover { background: #27AE60; color: #fff; transform: scale(1.08); }
.kpi-icon {
    width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 1.35rem;
}
.kpi-icon-orange { background: #FFF1E0; }
.kpi-icon-blue { background: #E3F2FD; }
.kpi-icon-green { background: #E8F8EF; }
.kpi-icon-red { background: #FDECEA; }
.kpi-icon-teal { background: #E6F7F3; }
.kpi-icon-indigo { background: #E8EAF6; }
.kpi-icon-purple { background: #F3E8FA; }
.kpi-value { font-size: 1.4rem; color: #1e293b; font-weight: 800; line-height: 1.2; }
.kpi-label { color: #64748b; font-size: .78rem; font-weight: 600; margin-top: 2px; }

.stat-label { color: #2c3e50; font-size: .875rem; margin-bottom: 8px; font-weight: 600; }
.stat-value { font-size: 1.75rem; color: #2c3e50; font-weight: bold; }
.stat-change { font-size: .75rem; margin-top: 4px; display: flex; align-items: center; gap: 4px; color: #94a3b8; }
.stat-change.positive { color: #27AE60; }

.charts-section { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 20px; margin-bottom: 20px; }
.chart-card { background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border: 1px solid #e2e8f0; overflow: hidden; }
.chart-card-wide { grid-column: 1 / -1; }
.chart-header { padding: 14px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.chart-title { margin: 0; font-size: .95rem; font-weight: 700; color: #34495E; }
.chart-title-note { font-size: .75rem; font-weight: 500; color: #94a3b8; }
.chart-body { padding: 16px; }

.efficiency-highlights { display: flex; flex-direction: column; gap: 14px; justify-content: center; height: 100%; min-height: 228px; }
.efficiency-highlight { background: #f8fafc; border-radius: 10px; padding: 10px 14px; border-left: 4px solid #27AE60; margin-top: 6px; }
.efficiency-highlight:first-of-type { margin-top: 8px; }

.leaderboard-card { background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, .08); border: 1px solid #e2e8f0; margin-bottom: 20px; overflow: hidden; }
.top-driver-banner {
    display: flex; align-items: center; gap: 16px; padding: 20px 24px;
    background: linear-gradient(135deg, #FFF6EC 0%, #FFEFDC 100%); border-bottom: 1px solid #e2e8f0;
}
.top-driver-medal { font-size: 2.4rem; }
.top-driver-label { font-size: .75rem; font-weight: 700; color: #B45309; text-transform: uppercase; letter-spacing: .05em; }
.top-driver-name { font-size: 1.3rem; font-weight: 800; color: #1e293b; margin-top: 2px; }
.top-driver-stats { font-size: .85rem; color: #64748b; margin-top: 4px; }

.driver-table-wrapper { padding: 4px 20px 16px; overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table thead { background: #f8fafc; }
.data-table th { padding: 10px 14px; text-align: left; color: #64748b; font-weight: 700; font-size: .72rem; text-transform: uppercase; letter-spacing: .05em; border-bottom: 2px solid #e2e8f0; }
.data-table tbody tr { border-bottom: 1px solid #e2e8f0; background: #fff; }
.data-table tbody tr:hover { background: #fff8f3; }
.data-table td { padding: 10px 14px; font-size: .875rem; color: #334155; }
.rank-cell { font-size: 1.1rem; font-weight: 700; }


.empty-state { text-align: center; padding: 60px 20px; background: #fff; border-radius: 12px; border: 1px solid #cbd5e1; }
.empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
.empty-title { font-size: 1.1rem; font-weight: 700; color: #34495E; margin-bottom: 6px; }
.empty-description { color: #64748b; font-size: .9rem; }

.trend-metric-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
.trend-metric-picker-label { font-size: .78rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .03em; margin-right: 4px; }
.chip-btn {
    padding: 5px 12px; border: 1px solid #cbd5e1; background: white; border-radius: 16px;
    font-size: .75rem; font-weight: 500; color: #64748b; cursor: pointer; transition: all .2s ease;
}
.chip-btn:hover { border-color: #D2691E; color: #D2691E; background: #fff9f5; }
.chip-btn.active { background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); color: white; border-color: #D2691E; }
.trend-metric-picker .chip-btn.active { color: #fff; }

/* KPI breakdown popup */
.simple-popup {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999;
    background: rgba(15, 23, 42, .72); backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
    display: flex; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box;
}
.popup-content { background: #fff; border-radius: 12px; width: 100%; max-width: 680px; max-height: 80vh; overflow-y: auto; box-shadow: 0 8px 32px rgba(0, 0, 0, .25); }
.popup-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%); border-radius: 12px 12px 0 0; gap: 10px; }
.popup-header h3 { color: white; margin: 0; font-size: 1.1rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.close-btn { background: none; border: none; color: white; font-size: 1.5rem; cursor: pointer; line-height: 1; flex-shrink: 0; }
.popup-body { padding: 20px; overflow-x: auto; }

@media (max-width: 1100px) {
    .analytics-grid { grid-template-columns: 1fr 1fr; }
    .analytics-trend-card { grid-column: 1 / -1; }
}

@media (max-width: 640px) {
    .header-content { flex-direction: column; align-items: flex-start; gap: 10px; }
    .main-content { padding: 12px; }
    .kpi-grid, .charts-section, .analytics-grid { grid-template-columns: 1fr; }
    .analytics-trend-card { grid-column: auto; }
    .hero-banner { flex-direction: column; align-items: flex-start; gap: 12px; }
    .hero-value { font-size: 2rem; }
    .date-range-popover { right: auto; left: 0; min-width: 220px; }
}
</style>
